import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef } from 'react'
import { Carousel } from 'react-responsive-carousel'
import { useToasts } from 'react-toast-notifications'

import { TMediaCallbacks } from '../../pages/CreatePost'
import PostMediaPlaceholder, { MEDIA_TYPE_TO_UPLOAD } from '../PostMediaPlaceholder'
import { TOAST_MESSAGE } from '../UI/ToastNotification/ToastNotification'

import { TExtendedPostAttachment, TImportCanvaPayload, TRemoveAttachmentPayload } from '../../store/managePost/newTypes'

import './UploadMultiContent.scss'
import InstagramMedialItem from './InstagramMedialItem'
import { TCanvaPayload } from '../PostCreation/CanvaButton/CanvaButton'
import { importTemporaryMediaFromCanva } from '../../store/managePost/thunks'
import { useDispatch } from '../../store'

const MAX_FILE_SIZE = 2e8 // 200 MB

// TODO simplify and rename
interface UploadVideoProps {
  attachments: TExtendedPostAttachment[]
  addMediaToPost: (image: any) => void
  refMediaCallbaks: RefObject<TMediaCallbacks>
  restrictions: any
  removeMediaFromStore: (id: TRemoveAttachmentPayload) => void
  isEditable: boolean
  isPreview: boolean
  isMokup?: boolean
}

const UploadMultiContent: FC<UploadVideoProps> = ({
  attachments,
  addMediaToPost,
  isMokup,
  refMediaCallbaks,
  isPreview,
  removeMediaFromStore,
  restrictions,
  isEditable
}) => {
  const dispatch = useDispatch()
  const refUploadMedia = useRef<HTMLInputElement>(null)
  const refVideoPlayer = useRef<HTMLVideoElement>(null)
  const refCarousel = useRef<{ prev: () => void }>({ prev: () => { } })

  const { addToast } = useToasts()

  const openMediaDialog = useCallback(() =>
    refUploadMedia.current?.click(),
    [refUploadMedia]
  )

  useEffect(() => {
    if (refUploadMedia.current && refMediaCallbaks.current) {
      refMediaCallbaks.current.addVideo = openMediaDialog
      refMediaCallbaks.current.addImage = openMediaDialog
    }
  }, [refUploadMedia, openMediaDialog])

  const onInputChange = async (event: any) => {
    const files = refUploadMedia.current?.files
    let currentFileCount = attachments.length

    if (files && files[0]) {
      Array.from(files).forEach((file) => {
        if (currentFileCount < restrictions?.message?.max_mediafiles) {
          currentFileCount += 1
          const reader = new FileReader()
          reader.onload = (e) => {
            refVideoPlayer.current?.load()
            refVideoPlayer.current?.play()
          }

          if (file.size < MAX_FILE_SIZE) {
            addMediaToPost({ file })
            reader.readAsDataURL(file)
          } else {
            addToast(TOAST_MESSAGE.MAX_FILE_SIZE_OF_VIDEO, { appearance: 'error' })
            refUploadMedia.current!.value = ''
          }
        }
      })
    }
  }

  const allowedMediaData = useMemo(() => {
    const type = restrictions.mediaRestrictions.image && restrictions.mediaRestrictions.video ?
      MEDIA_TYPE_TO_UPLOAD.MULTY_CONTENT : restrictions.mediaRestrictions.image ?
        MEDIA_TYPE_TO_UPLOAD.IMAGE : MEDIA_TYPE_TO_UPLOAD.VIDEO
    const acceptedTypes = []

    const mediaRestrictions = Object.entries(restrictions.mediaRestrictions) as [entry: string, data: any]
    for (let [entry, data] of mediaRestrictions) {
      if (data) {
        if (data.valid_file_type) {
          acceptedTypes.push(data.valid_file_type.map((type: string) => `${entry}/${type}`))
        } else {
          acceptedTypes.push(`${entry}/*`)
        }
      }
    }

    return {
      type,
      acceptedTypes: acceptedTypes.join(', ')
    }

  }, [restrictions])

  const updateCanvaImage = async ({
    exportUrl: content_url,
    designId: canva_design_id
  }: TCanvaPayload) => {
    const payload: TImportCanvaPayload = {
      content_url,
      canva_design_id
    }
    await dispatch(importTemporaryMediaFromCanva(payload))
  }

  return (
    <div className='upload-post-media'>
      <div className='upload-post-media__wrapper'>
        {!attachments.length ? (
          <PostMediaPlaceholder mediaType={allowedMediaData.type} onClick={openMediaDialog} />
        ) : (
          <Carousel
            showIndicators={attachments.length > 1}
            dynamicHeight={true}
            showStatus={false}
            showThumbs={false}
            renderArrowNext={() => null}
            renderArrowPrev={(...props) => {
              refCarousel.current.prev = props[0]
              return null
            }}
            centerSlidePercentage={100}
          >
            {attachments.map((attachment, index) => {
              const attachmentIds = { id: attachment.id, temporaryId: attachment.temporaryId }
              const deattachAttachment = () => {
                refCarousel.current?.prev()
                removeMediaFromStore(attachmentIds)
              }
              return (
                <InstagramMedialItem
                  {...{
                    attachment,
                    refMediaCallbaks,
                    restrictions,
                    isMokup,
                    isPreview,
                    isEditable,
                    deattachAttachment,
                    onUpdateCanvaImage: updateCanvaImage,
                    key: index
                  }}
                />
              )
            })}
          </Carousel>
        )}
        <input
          className='upload-post-media__input'
          multiple={allowedMediaData.type === MEDIA_TYPE_TO_UPLOAD.MULTY_CONTENT}
          accept={allowedMediaData.acceptedTypes}
          ref={refUploadMedia}
          type='file'
          onChange={onInputChange}
        />
      </div>
    </div>
  )
}

export default UploadMultiContent
